import i18n, { getNavigatorLanguageOrFallback } from 'internationalization/i18n';
import React, { useContext, useEffect, useState } from 'react';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { EnvironmentSettingsType, SettingsContext, SettingsContextProviderProps, UserPreferenceType, createEnvironmentSettingsObject, createUserPreferenceObject } from '../settings/SettingsTypes';
import { encryptData } from 'services/authentication/encryptData';
import { useSocketMessage } from 'services/api/updatesSocket';
import { localeOptions } from 'internationalization/i18n';
import { currencySymbols } from 'internationalization/currencySymbols';
import { ResolvedTheme, applyThemeMode, getStoredThemeMode, resolveTheme, storeThemeMode } from 'services/theme/themeUtils';

// The interval in milliseconds to re-evaluate the auto theme for the night hours
const THEME_CHECK_INTERVAL = 60000;

/*
 * SettingsContext.tsx
 * This context is created to contain states and information about user
 * and environment settings, such as language, job or scheduling settings.
 * This context also initialises the language based on the browser language
 * or sets it automatically to the users set up language.
 */ 

export const SettingsContextProvider: React.FC<SettingsContextProviderProps> = ({ children }) => {
    const { isLoggedIn, userHash, environmentHash, userDivision, setFirstName, setLastName, handleLogout } = useAuthContext();
    const { message: userPreferencesWebsocket } = useSocketMessage(['user_preferences_updated']);
    const { message: environmentSettingsWebsocket } = useSocketMessage(['environment_settings_updated']);
    const [userPreferences, setUserPreferences] = useState<UserPreferenceType>(createUserPreferenceObject);
    const [environmentSettings, setEnvironmentSettings] = useState<EnvironmentSettingsType>(createEnvironmentSettingsObject);
    const [_, _setLanguage] = useState<string>(getNavigatorLanguageOrFallback(navigator.language, 'language'));
    const [defaultCurrency, setDefaultCurrency] = useState<string>('eur');
    const [defaultCurrencySymbol, setDefaultCurrencySymbol] = useState<string>('€');
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(getStoredThemeMode()));
    
    // Set the timezone based on the browser timezone on first render
    useEffect(() => {
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setUserPreferences(prev => ({ ...prev, ['timezone']: detectedTimezone }));
    }, []);

    // Reset to browser's language if not logged in
    useEffect(() => {
        if (!isLoggedIn) {
            setLanguage(getNavigatorLanguageOrFallback(navigator.language, 'language'));
        }
    }, [isLoggedIn]);

    // Fetch user preferences when login is true
    useEffect(() => {
        if (isLoggedIn && userHash) {
            const fetchUserPreferences = async () => {
                try {
                    // Fetch the user preferences
                    const fetchedPreferences = await fetchData({ apiUrl: `get_user_preference/${userHash}`, handleLogout })

                    if (!fetchedPreferences.user_locale) {
                        fetchedPreferences.user_locale = getNavigatorLanguageOrFallback(navigator.language, 'locale');
                    }

                    if (!fetchedPreferences.language) {
                        fetchedPreferences.language = getNavigatorLanguageOrFallback(navigator.language, 'language');
                    }

                    // Update the user preferences and language in the context
                    setUserPreferences(prev => ({ ...prev, ...fetchedPreferences }));
                    setLanguage(fetchedPreferences.language);

                    // Convert the user preferences to a string and store it encrypted in the local storage
                    const userPreferencesString = JSON.stringify(fetchedPreferences)
                    localStorage.setItem('user_preferences', encryptData(userPreferencesString))
                } catch (error: any) {
                    console.error(error);
                }
            }
            fetchUserPreferences();
        }
    }, [isLoggedIn, userHash]);

    // Fetch user preferences when login is true
    useEffect(() => {
        if (isLoggedIn && environmentHash) {
            const fetchEnvironmentSettings = async () => {
                try {
                    // Fetch the environment settings with the division values of the user division
                    const fetchedSettings = await fetchData({
                        apiUrl: `get_environment_settings/${environmentHash}`,
                        params: userDivision ? { division: userDivision } : undefined,
                        handleLogout
                    })

                    // Set the user preferences and language in the context
                    setEnvironmentSettings(fetchedSettings);

                    // Convert the environment settings to a string and store it encrypted in the local storage
                    const settingsString = JSON.stringify(fetchedSettings)
                    localStorage.setItem('env_settings', encryptData(settingsString))                  
                } catch (error: any) {
                    console.error(error);
                }
            }
            fetchEnvironmentSettings();
        }
    }, [isLoggedIn, environmentHash, userDivision]);

    // Receive updates to user preferences by websocket
    useEffect(() => {
        const { type, message } = userPreferencesWebsocket || {};

        if (type === 'user_preferences_updated' ) {
            // Update the user preferences
            setUserPreferences(prev => ({ ...prev, ...message }));
            
            // Update the language
            if (message.language) {
                setLanguage(message.language)
            }

            // Update the first name
            if (message.first_name) {
                setFirstName(message.first_name)
                localStorage.setItem('first_name', encryptData(message.first_name))
            }

            // Update the last name
            if (message.last_name) {
                setLastName(message.last_name)
                localStorage.setItem('last_name', encryptData(message.last_name))
            }

            // Convert the user preferences to a string and store it encrypted in the local storage
            const messageString = JSON.stringify(message)
            localStorage.setItem('user_preferences', encryptData(messageString))
        }
    }, [userPreferencesWebsocket]);

    // Receive updates to environment settings by websocket
    useEffect(() => {
        const { type, message } = environmentSettingsWebsocket || {};

        if (type === 'environment_settings_updated' ) {
            // Ignore division tagged updates that are not for the division of the user
            if (message && 'division' in message && message.division !== null) {
                if (!userDivision || message.division !== userDivision) {
                    return;
                }
            }

            // Remove the division marker before merging the settings
            const { division, ...settingsMessage } = message || {};

            // Update the environment settings
            setEnvironmentSettings(prev => ({ ...prev, ...settingsMessage }));

            // Convert the environment settings to a string and store it encrypted in the local storage
            const messageString = JSON.stringify(settingsMessage)
            localStorage.setItem('env_settings', encryptData(messageString))
        }
    }, [environmentSettingsWebsocket, userDivision]);

    // Apply the theme mode of the user and follow the system setting and the clock in auto mode
    useEffect(() => {
        const themeMode = userPreferences.theme || 'auto';

        // Apply and store the mode right away
        storeThemeMode(themeMode);
        setResolvedTheme(applyThemeMode(themeMode));

        // A fixed mode needs no listeners
        if (themeMode !== 'auto') return;

        // Re-apply the theme when the system setting changes or the night hours start or end
        const reapplyTheme = () => setResolvedTheme(applyThemeMode(themeMode));
        const mediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
        mediaQuery?.addEventListener('change', reapplyTheme);
        const interval = setInterval(reapplyTheme, THEME_CHECK_INTERVAL);
        return () => {
            mediaQuery?.removeEventListener('change', reapplyTheme);
            clearInterval(interval);
        };
    }, [userPreferences.theme]);

    // Update the default currency and symbol when user preferences are updated
    useEffect(() => {
        const currency = userPreferences.default_currency || 'eur';
        setDefaultCurrency(currency);

        const symbol = currency ? currencySymbols[currency.toLowerCase()] || '€' : '€';
        setDefaultCurrencySymbol(symbol);
    }, [userPreferences.default_currency]);

    // Set the language
    const setLanguage = (language: string) => {
        _setLanguage(language);
        i18n.changeLanguage(language)
    }

    // Get the time format of the user locale
    const getTimeFormat = (): '12h' | '24h' => {
        return localeOptions[userPreferences.user_locale].timeFormat;
    }

    return (
        <SettingsContext.Provider value={({ 
            userPreferences, 
            setUserPreferences,
            resolvedTheme,
            environmentSettings, 
            setEnvironmentSettings,
            setLanguage, 
            userLocale: userPreferences.user_locale, 
            userTimezone: userPreferences.timezone, 
            getTimeFormat,
            defaultCurrency,
            defaultCurrencySymbol
        })}>
                {children}
        </SettingsContext.Provider>
    );
};

// Create a custom hook for the settings context
export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within the SettingsContextProvider');
    }
    return context;
};